/**
 * /community/review/new?product=<sg product slug> — the owner review (code name: owner note)
 * written from the community (design 5b), and the reward screen after an accepted publish
 * (design 5c). The form is the product page's own `OwnerNoteBand`, hosted here with a live
 * preview of the feed row and a "When you publish" panel; there is still one form.
 *
 * The server resolves the product the way the product page does (`resolveDealHuntPage`: node
 * id, name, image, brand and the owners count in one read) and renders nothing of the reader:
 * the HTML is shared-cached like every community page. Edit mode, the panel's standing and the
 * reward hydrate from `/me` and the publish answer. `noindex` — a form, not content.
 * Module map: `lib/community/README.md`.
 *
 * The handoff params (`?buy_again=&src=&from=`, contract §7) are validated here and passed down;
 * a value that fails is absent. They vary the HTML by URL only, never by reader.
 */
import { useEffect, useState } from 'react'
import type { GetServerSideProps } from 'next'
import { publicConfig } from 'config'
import { fetchCommunitySummary, isAudioSlug, resolveDealHuntPage } from 'lib/dealhunt/client'
import { dealHuntProductHref, productDisplayName, productSlug, withSlash } from 'lib/dealhunt/url'
import { communityHref, safeImageUrl } from 'lib/community/model'
import { CommunityShell, communityServerProps, type CommunityPageProps } from 'lib/community/page'
import {
  REVIEW_NEW_PATH,
  reviewProductParam,
  type CommunityPointsTable,
} from 'lib/community/review'
import { writerCrumb, writerHandoffParams, type WriterHandoff } from 'lib/community/writer-handoff'
import Crumbs from 'components/community/Crumbs'
import ReviewComposer, { type ReviewProduct } from 'components/community/ReviewComposer'
import styles from 'components/community/community.module.css'

interface ReviewPageData {
  product: ReviewProduct
  /** The summary's points table and founding counter; null when the summary could not be read
   *  — the form still works, the panel then names no points. */
  points: CommunityPointsTable | null
  founding: { count: number; cap: number } | null
  handoff: WriterHandoff
}

const NewReviewPage: React.VFC<CommunityPageProps<ReviewPageData>> = ({
  common,
  ts,
  data: { product, points, founding, handoff },
}) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  // "New" in the shared HTML; "Edit" once `/me` says the reader has a review here.
  const [editing, setEditing] = useState(false)
  useEffect(() => setEditing(false), [product.slug])
  return (
    <CommunityShell
      common={common}
      ts={ts}
      path={withSlash(REVIEW_NEW_PATH, trailingSlash)}
      title={`Your owner review of the ${product.name}`}
      description={`Tell the next buyer what owning the ${product.name} is like: a few quick answers and one sentence.`}
      index={false}
    >
      <div className={styles.page}>
        <Crumbs
          items={[
            { label: 'Home', href: common.homeLink.href },
            { label: 'Community', href: communityHref(trailingSlash) },
            { label: writerCrumb(editing) },
          ]}
        />
        <ReviewComposer
          // "Change" navigates to another product on the same page: a fresh composer, band and
          // draft for it, never the previous product's state.
          key={product.slug}
          product={product}
          points={points}
          founding={founding}
          handoff={handoff}
          onEditingKnown={setEditing}
        />
      </div>
    </CommunityShell>
  )
}

export const getServerSideProps: GetServerSideProps<
  CommunityPageProps<ReviewPageData>
> = async context => {
  // Validated before any fetch: a made-up value costs DealHunt nothing.
  const slug = reviewProductParam(context.query.product)
  if (!slug) {
    return { notFound: true }
  }
  const handoff = writerHandoffParams(context.query)
  return communityServerProps(context, async () => {
    // The resolve defines the page and lets `UpstreamError` propagate (a 500, never a 404 for a
    // product that exists). The summary only feeds the panel: a failure costs the points lines.
    const [page, summary] = await Promise.all([
      resolveDealHuntPage([slug]),
      fetchCommunitySummary().catch(() => null),
    ])
    // Owner reviews are audio-only: a category, or a product outside audio, has no form.
    if (!page || page.kind !== 'product' || !isAudioSlug(page.node.fullSlug)) {
      return null
    }
    const trailingSlash = publicConfig('useTrailingSlash')
    const brand = page.brandNode?.name ?? null
    const brandSlug = page.brandNode?.slug ?? null
    return {
      product: {
        // The product page's ids and naming (`ProductPageTemplate` → `OwnersSection`).
        nodeId: page.node.id,
        name: productDisplayName(page),
        slug: productSlug(brand, page.node.fullSlug, brandSlug),
        href: dealHuntProductHref(page.node.fullSlug, brand, trailingSlash, brandSlug),
        imageUrl: safeImageUrl(page.node.imageUrl),
        brand,
        publishedCount: page.enrichment.owners?.published_count ?? 0,
      },
      points: summary?.points ?? null,
      founding: summary?.founding ?? null,
      handoff,
    }
  })
}

export default NewReviewPage
